import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Typography,
  IconButton,
  Snackbar,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Card,
  CardContent,
  CardActions,
  Chip,
  Tooltip,
  Pagination
} from "@mui/material";
import Autocomplete from '@mui/material/Autocomplete';
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import EventIcon from "@mui/icons-material/Event";
import PlaceIcon from "@mui/icons-material/Place";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import SaveIcon from "@mui/icons-material/Save";
import axiosInstance from "API/axiosInstance";
import { decryptText } from "config/encryption";
import { API_ENDPOINTS } from "config/api";
import MDButton from "components/MDButton";

// Get staffId from localStorage (token is handled by axiosInstance)
const getAdminSession = () => {
  try {
    const session = localStorage.getItem("adminLogsessiondetails") || localStorage.getItem("adminsessiondetails");
    if (session) {
      const decrypted = decryptText(session);
      if (decrypted) {
        const parsed = JSON.parse(decrypted);
        return {
          staffId: parsed.StaffId || parsed.staffId || parsed.StaffCode || parsed.staffCode || null
        };
      }
    }
  } catch (error) {
    console.error("Error extracting admin session:", error);
  }
  return { staffId: null };
};

const AdminEventManager = ({ onClose }) => {
  const { staffId } = getAdminSession();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editEvent, setEditEvent] = useState(null);
  const [form, setForm] = useState({
    eventId: 0,
    chapterId: "",
    eventName: "",
    eventDate: "",
    venue: "",
    description: "",
    createdUserId: 1
  });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, eventId: null });
  const [page, setPage] = useState(1);
  const cardsPerPage = 2;
  const [chapterOptions, setChapterOptions] = useState([]);

  const fetchEvents = async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get(API_ENDPOINTS.ADMIN.GET_CHAPTER_EVENTS_ADMIN);
      setEvents(res.data);
    } catch (err) {
      console.error("Error fetching events:", err);
      setSnackbar({ open: true, message: "Failed to fetch events", severity: "error" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEvents();
    // Fetch chapter dropdown
    const fetchChapters = async () => {
      try {
        const res = await axiosInstance.get(API_ENDPOINTS.ADMIN.ADMIN_CHAPTER_DROPDOWN_DETAILS);
        setChapterOptions(res.data.chapters || []);
      } catch (error) {
        console.error("Error fetching chapters:", error);
      }
    };
    fetchChapters();
  }, []);

  useEffect(() => {
    const totalPages = Math.ceil(events.length / cardsPerPage);
    if (page > totalPages) {
      setPage(totalPages || 1);
    } else if (page < 1) {
      setPage(1);
    }
  }, [events, page]);

  const handleSnackbarClose = () => setSnackbar({ ...snackbar, open: false });

  const handleDialogOpen = (event = null) => {
    setEditEvent(event);
    setForm(event ? { ...event } : {
      eventId: 0,
      chapterId: "",
      eventName: "",
      eventDate: "",
      venue: "",
      description: "",
      createdUserId: staffId
    });
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setEditEvent(null);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    try {
      const payload = { ...form, createdUserId: staffId };
      const res = await axiosInstance.post(API_ENDPOINTS.ADMIN.MERGE_CHAPTER_EVENT, payload);
      setSnackbar({ open: true, message: res.data.message || "Saved successfully", severity: "success" });
      setDialogOpen(false);
      fetchEvents();
    } catch (err) {
      console.error("Error saving event:", err);
      setSnackbar({ open: true, message: "Failed to save event", severity: "error" });
    }
  };

  const handleDelete = async () => {
    try {
      const res = await axiosInstance.post(API_ENDPOINTS.ADMIN.DELETE_CHAPTER_EVENT, {
        eventId: deleteDialog.eventId,
        deletedBy: staffId
      });
      setSnackbar({ open: true, message: res.data.message || "Deleted successfully", severity: "success" });
      setDeleteDialog({ open: false, eventId: null });
      fetchEvents();
    } catch (err) {
      console.error("Error deleting event:", err);
      setSnackbar({ open: true, message: "Failed to delete event", severity: "error" });
    }
  };

  return (
    <Box sx={{ width: 400, maxWidth: '100vw', p: 3, position: 'relative', bgcolor: '#f8f9fa', minHeight: '100vh' }}>
      <Box sx={{ position: 'absolute', top: 8, right: 8, zIndex: 2 }}>
        <Tooltip title="Close">
          <IconButton onClick={onClose} color="error">
            <CloseIcon />
          </IconButton>
        </Tooltip>
      </Box>
      <Typography variant="h5" fontWeight="bold" mb={2}>Manage Events</Typography>
      <MDButton color="info" variant="gradient" startIcon={<AddIcon />} onClick={() => handleDialogOpen()} sx={{ mb: 2 }}>
        Add Event
      </MDButton>
      {loading ? <CircularProgress /> : (
        <>
          <Grid container spacing={3} justifyContent="center">
            {events.slice((page-1)*cardsPerPage, page*cardsPerPage).map((event) => (
              <Grid item xs={12} key={event.eventId}>
                <Card sx={{ width: '100%', borderRadius: 3, boxShadow: 3, bgcolor: '#fff', minHeight: 220, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', transition: 'all 0.3s ease', '&:hover': { boxShadow: 6, transform: 'translateY(-4px)' } }}>
                  <CardContent>
                    <Box display="flex" alignItems="center" mb={1}>
                      <EventIcon color="primary" sx={{ mr: 1 }} />
                      <Typography variant="h6" fontWeight="bold">{event.eventName}</Typography>
                    </Box>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      <CalendarTodayIcon fontSize="small" sx={{ mr: 0.5, verticalAlign: 'middle' }} />
                      {event.eventDate ? new Date(event.eventDate).toLocaleDateString() : ''}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" mb={1}>{event.description}</Typography>
                    <Box display="flex" flexWrap="wrap" gap={1} mb={1}>
                      <Chip label={event.chapterName} size="small" color="info" />
                      <Chip label={event.venue} size="small" icon={<PlaceIcon />} />
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      Created: {event.createdAt ? new Date(event.createdAt).toLocaleString() : ''}
                    </Typography>
                  </CardContent>
                  <CardActions sx={{ justifyContent: 'flex-end' }}>
                    <Tooltip title="Edit">
                      <IconButton onClick={() => handleDialogOpen(event)} color="info">
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton color="error" onClick={() => setDeleteDialog({ open: true, eventId: event.eventId })}>
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
          <Box display="flex" justifyContent="center" mt={3}>
            <Pagination
              count={Math.ceil(events.length / cardsPerPage)}
              page={page}
              onChange={(e, value) => setPage(value)}
              color="info"
              shape="rounded"
            />
          </Box>
        </>
      )}
      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={handleDialogClose} fullWidth maxWidth="sm">
        <DialogTitle>{editEvent ? 'Edit Event' : 'Add Event'}</DialogTitle>
        <DialogContent>
          <TextField margin="dense" label="Event Name" name="eventName" value={form.eventName} onChange={handleFormChange} fullWidth />
          <TextField margin="dense" label="Event Date" name="eventDate" type="date" value={form.eventDate ? form.eventDate.substring(0,10) : ''} onChange={handleFormChange} fullWidth InputLabelProps={{ shrink: true }} />
          <TextField margin="dense" label="Venue" name="venue" value={form.venue} onChange={handleFormChange} fullWidth />
          <TextField margin="dense" label="Description" name="description" value={form.description} onChange={handleFormChange} fullWidth />
          <Autocomplete
            options={chapterOptions}
            getOptionLabel={(option) => option.txt || ''}
            value={chapterOptions.find((ch) => ch.id === form.chapterId) || null}
            onChange={(_, value) => setForm((prev) => ({ ...prev, chapterId: value ? value.id : '' }))}
            renderInput={(params) => (
              <TextField {...params} label="Chapter" margin="dense" fullWidth />
            )}
            isOptionEqualToValue={(option, value) => option.id === value.id}
          />
        </DialogContent>
        <DialogActions>
          <MDButton onClick={handleDialogClose} color="info" variant="outlined">Cancel</MDButton>
          <MDButton onClick={handleSave} color="info" variant="gradient" startIcon={<SaveIcon />}>Save</MDButton>
        </DialogActions>
      </Dialog>
      {/* Delete Dialog */}
      <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false, eventId: null })}>
        <DialogTitle>Delete Event?</DialogTitle>
        <DialogContent>Are you sure you want to delete this event?</DialogContent>
        <DialogActions>
          <MDButton onClick={() => setDeleteDialog({ open: false, eventId: null })} color="info" variant="outlined">Cancel</MDButton>
          <MDButton onClick={handleDelete} color="error" variant="contained">Delete</MDButton>
        </DialogActions>
      </Dialog>
      {/* Snackbar */}
      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={handleSnackbarClose} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

AdminEventManager.propTypes = {
  onClose: PropTypes.func.isRequired,
};

export default AdminEventManager; 